// Owner one-tap Mobile Go Live — used from Owner HQ Overview.
import { useRef, useState } from "react";
import { toast } from "sonner";
import { X, Radio, Smartphone } from "lucide-react";
import api from "../../lib/api";
import LiveKitSetupCard, { LiveKitConnectModal } from "../../components/LiveKitSetupCard";

export function OwnerMobileGoLive({ onClose }) {
  const [title, setTitle] = useState("Owner takeover — going live");
  const [perms, setPerms] = useState({ mic: false, cam: false, checking: false });
  const [showLkModal, setShowLkModal] = useState(false);
  const lkCardRef = useRef(null);

  const requestPerms = async () => {
    setPerms((p) => ({ ...p, checking: true }));
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: true });
      stream.getTracks().forEach((t) => t.stop());
      setPerms({ mic: true, cam: true, checking: false });
      toast.success("Mic + camera ready");
    } catch (e) {
      setPerms({ mic: false, cam: false, checking: false });
      toast.error("Browser denied mic/camera access");
    }
  };
  const fire = async () => {
    // Per-user LiveKit gate — open the embedded card in-place if no project connected.
    try {
      const { data } = await api.get("/me/livekit/projects");
      if (!data?.projects?.length) {
        setShowLkModal(true);
        return;
      }
    } catch { /* soft-fail — backend will still reject if unconnected */ }
    try {
      const { data } = await api.post("/streams/mobile_golive", { title, camera_only: true });
      toast.success("You're live! Opening Watch view…");
      onClose();
      setTimeout(() => window.location.href = `/watch/${data.id}`, 600);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Failed to go live";
      toast.error(msg, { duration: 8000 });
      if (/livekit|live streaming|connect your/i.test(msg)) setShowLkModal(true);
    }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-6" onClick={onClose}>
      <div className="glass-strong p-6 max-w-md w-full max-h-[90vh] overflow-y-auto space-y-4" onClick={(e) => e.stopPropagation()} data-testid="owner-mobile-modal">
        <div className="flex items-center justify-between"><h3 className="font-display text-xl flex items-center gap-2"><Smartphone className="w-5 h-5 text-nexus-amber" />Go Live (Mobile / Instant)</h3><button onClick={onClose}><X className="w-4 h-4" /></button></div>

        {/* LiveKit connection card ABOVE the go-live form so users can connect without leaving this modal */}
        <LiveKitSetupCard ref={lkCardRef} compact />

        <div className="border border-nexus-cyan/30 bg-nexus-cyan/5 p-3 space-y-2" data-testid="mobile-perm-banner">
          <div className="text-[10px] tracking-[0.25em] uppercase text-nexus-cyan font-display">Going live activates mic + camera</div>
          <p className="text-[11px] text-white/60 leading-relaxed">Your browser will ask permission to use your microphone and camera. Tap <span className="text-white">Allow</span>.</p>
          <div className="flex items-center gap-2 text-[11px]">
            <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 border ${perms.mic ? "border-nexus-mint/60 text-nexus-mint" : "border-white/10 text-white/40"}`}><span>Mic</span> {perms.mic ? "✓" : "○"}</span>
            <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 border ${perms.cam ? "border-nexus-mint/60 text-nexus-mint" : "border-white/10 text-white/40"}`}><span>Cam</span> {perms.cam ? "✓" : "○"}</span>
            <button onClick={requestPerms} disabled={perms.checking} className="btn-ghost ml-auto" data-testid="mobile-perm-request">{perms.checking ? "…" : "Test"}</button>
          </div>
        </div>
        <p className="text-xs text-white/50">Start an instant chat-only stream from any device. Uses your browser camera + LiveKit.</p>
        <input value={title} onChange={(e) => setTitle(e.target.value)} className="w-full bg-transparent border border-white/10 px-3 py-2 text-sm" placeholder="Stream title" />
        <button onClick={fire} className="btn-nexus w-full justify-center" data-testid="mobile-fire"><Radio className="w-3.5 h-3.5" />Go Live Now</button>

        <LiveKitConnectModal
          open={showLkModal}
          onClose={() => setShowLkModal(false)}
          onSetupClick={() => lkCardRef.current?.openAndScroll()}
        />
      </div>
    </div>
  );
}
